import { useEffect, useMemo, useState } from 'react';
import type { IReportType, IReportGroup, IReportAhuRow } from 'app/services/reportsBoardApi';
import { reportBtnClasses, SECTION_REPORTS } from '../reportScopes';

/**
 * Left report menu — 1:1 port of report/mvc/view/menu.js + tmpl/menu.tmpl.
 *
 * Structure (menu.tmpl): a search label, then one <li> per group — "All
 * Reports" first (every report), then the fetched groups each holding its own
 * reports. Collapse/expand is CSS-driven by the hidden group checkboxes
 * (`#main_menu > ul > li > input:checked ~ .sublist`, report styles.css:1009),
 * so the markup must keep input → label → ul.sublist as direct li children.
 *
 * Behaviors ported:
 *  - groups with zero visible reports hidden (menu.js:79-85 → li.hide).
 *  - one-open-at-a-time group toggle (clickToGroup, menu.js:115-121).
 *  - text search: #main_menu gets .searching, matching All-Reports buttons get
 *    .search-resault (menu.js:148-166; CSS does the show/hide).
 *  - per-AHU report enablement: the selected AHU's `reports` flags add
 *    non-clickable (+ hide for the four section reports) to buttons whose
 *    VALUE matches a falsy flag key (menu.js:129-146). Cleared when no single
 *    AHU is selected (activateProjectReports/activateAHUReports reset).
 *  - selecting a report checks its group's checkbox and unchecks All Reports
 *    (controller/action.js:85-89) — also applied for deep links.
 */
interface ReportsMenuProps {
  reports: IReportType[];
  groups: IReportGroup[];
  currentReportValue: string | null;
  /** Single-selected AHU row (deep link / auto-selected single main) or null. */
  selectedAhu: IReportAhuRow | null;
  onSelectReport: (value: string) => void;
}

const ReportsMenu = ({
  reports,
  groups,
  currentReportValue,
  selectedAhu,
  onSelectReport,
}: ReportsMenuProps) => {
  const [search, setSearch] = useState('');
  // Checked state of the group checkboxes, keyed by group VALUE ('all' for
  // the All Reports group).
  const [checkedGroups, setCheckedGroups] = useState<Record<string, boolean>>({});

  // Groups without any report hidden (menu.js:79-85).
  const groupHasReports = useMemo(() => {
    const existed: Record<string, boolean> = {};
    reports.forEach((r) => {
      existed[r.group] = true;
    });
    return existed;
  }, [reports]);

  // open:report side effect (action.js:85-89): check the selected report's
  // group, uncheck #report_all. Runs for menu clicks AND deep links.
  useEffect(() => {
    if (!currentReportValue) return;
    const report = reports.find((r) => r.value === currentReportValue);
    if (!report) return;
    const group = groups.find((g) => g.name === report.group);
    if (!group) return;
    setCheckedGroups((prev) => ({ ...prev, all: false, [group.value]: true }));
  }, [currentReportValue, reports, groups]);

  // clickToGroup (menu.js:115-121): uncheck every group, then apply the
  // clicked checkbox's new state — radio behavior that can also close itself.
  const onGroupToggle = (key: string, checked: boolean) => {
    setCheckedGroups({ [key]: checked });
  };

  // Per-AHU enablement map (menu.js:129-146). Only applies when a real single
  // AHU is selected and its row carries `reports` flags.
  const gating = useMemo(() => {
    const nonClickable: Record<string, boolean> = {};
    const hidden: Record<string, boolean> = {};
    const flags = selectedAhu?.reports;
    if (selectedAhu && flags) {
      Object.keys(flags).forEach((key) => {
        if (!flags[key]) {
          nonClickable[key] = true;
          if (SECTION_REPORTS.includes(key)) hidden[key] = true;
        }
      });
    }
    return { nonClickable, hidden };
  }, [selectedAhu]);

  // search (menu.js:148-166): case-insensitive substring on the button TEXT;
  // marks only the All-Reports copies (legacy selector scopes '.all_reports').
  const searchLower = search.toLowerCase();
  const isSearchResult = (name: string) =>
    name.toLowerCase().indexOf(searchLower) !== -1;

  const btnClassName = (r: IReportType, inAllReports: boolean) => {
    const cls = reportBtnClasses(r);
    if (r.value === currentReportValue) cls.push('current_report');
    if (gating.nonClickable[r.value]) cls.push('non-clickable');
    if (gating.hidden[r.value]) cls.push('hide');
    if (inAllReports && search && isSearchResult(r.name)) cls.push('search-resault');
    return cls.join(' ');
  };

  const renderButton = (r: IReportType, inAllReports: boolean) => (
    <li key={r.id}>
      <button
        type="button"
        value={r.value}
        data-group={r.group}
        data-name={r.name}
        className={btnClassName(r, inAllReports)}
        onClick={() => {
          // clickMenuItem (menu.js:96-113): hash query is built by the page;
          // the sessionStorage resets mirror the legacy click side effects.
          sessionStorage.removeItem('genereate-report-data');
          sessionStorage.removeItem('is-generate');
          onSelectReport(r.value);
        }}
      >
        {r.name}
      </button>
    </li>
  );

  return (
    <div id="main_menu" className={search ? 'searching' : ''}>
      <label className="report-search">
        <input
          type="search"
          className="report_search"
          placeholder="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <i className="icon-search-1" />
        <span className="placeholder-label-search">Search</span>
      </label>
      <ul>
        <li>
          <input
            id="report_all"
            className="hidden report_group"
            type="checkbox"
            checked={!!checkedGroups.all}
            onChange={(e) => onGroupToggle('all', e.target.checked)}
          />
          <label htmlFor="report_all" className="ka-has-tooltip" tooltip="All Reports">
            <i className="icon-folder-open" />
            <span>All Reports</span>
            <i className="icon-add" />
          </label>
          <ul className="sublist all_reports">
            {reports.map((r) => renderButton(r, true))}
          </ul>
        </li>
        {groups.map((g) => (
          <li key={g.id} className={groupHasReports[g.name] ? '' : 'hide'}>
            <input
              id={`report_${g.value}`}
              className="hidden report_group"
              type="checkbox"
              checked={!!checkedGroups[g.value]}
              onChange={(e) => onGroupToggle(g.value, e.target.checked)}
            />
            <label
              htmlFor={`report_${g.value}`}
              className="ka-has-tooltip"
              tooltip={g.name}
            >
              <i className="icon-folder-open" />
              <span>{g.name}</span>
              <i className="icon-add" />
            </label>
            <ul className="sublist">
              {reports.filter((r) => r.group === g.name).map((r) => renderButton(r, false))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ReportsMenu;
